
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";

const supportFormSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  issueType: z.string().min(1, "Please select an issue type"),
  message: z.string().min(10, "Message must be at least 10 characters"),
});

type SupportFormValues = z.infer<typeof supportFormSchema>;

const SupportForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<SupportFormValues>({
    resolver: zodResolver(supportFormSchema),
  });

  const onSubmit = async (data: SupportFormValues) => {
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.functions.invoke("send-support-email", {
        body: { 
          name: data.name,
          email: data.email,
          issueType: data.issueType,
          message: data.message
        }
      });

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: "Support request submitted!",
        description: "Thank you for reaching out. Our team will get back to you soon.",
      });
      
      reset();
    } catch (error) {
      console.error("Error sending support request:", error);
      toast({
        title: "Error submitting request",
        description: "Please try again later or contact us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="name">
          Name <span className="text-red-500">*</span>
        </Label>
        <Input
          id="name"
          {...register("name")}
          placeholder="Your name"
        />
        {errors.name && (
          <p className="text-sm text-red-500">{errors.name.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">
          Email <span className="text-red-500">*</span>
        </Label>
        <Input
          id="email"
          type="email"
          {...register("email")}
          placeholder="your.email@example.com"
        />
        {errors.email && (
          <p className="text-sm text-red-500">{errors.email.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="issueType">
          Issue Type <span className="text-red-500">*</span>
        </Label>
        <Select 
          onValueChange={(value) => setValue("issueType", value)}
        >
          <SelectTrigger id="issueType">
            <SelectValue placeholder="Select issue type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="account">Account Issue</SelectItem>
            <SelectItem value="reagent">Reagent Listing Problem</SelectItem>
            <SelectItem value="swap">Swap Process Issue</SelectItem>
            <SelectItem value="payment">Credits & Payment</SelectItem>
            <SelectItem value="bug">Bug Report</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
        <input type="hidden" {...register("issueType")} />
        {errors.issueType && (
          <p className="text-sm text-red-500">{errors.issueType.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="message">
          Describe Your Issue <span className="text-red-500">*</span>
        </Label>
        <Textarea
          id="message"
          {...register("message")}
          placeholder="Please provide details about your issue"
          className="min-h-[150px]"
        />
        {errors.message && (
          <p className="text-sm text-red-500">{errors.message.message}</p>
        )}
      </div>

      <Button 
        type="submit" 
        disabled={isSubmitting}
        className="w-full md:w-auto"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Submitting...
          </>
        ) : (
          "Submit Support Request"
        )}
      </Button>
    </form>
  );
};

export default SupportForm;
